import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import usePlacesAutocomplete, {
    getGeocode,
    getLatLng,
} from 'use-places-autocomplete';
import { Search, MapPin, Loader2, Navigation } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from 'sonner';

const libraries = ['places', 'geometry'];

const mapContainerStyle = {
    width: '100%',
    height: '400px',
    borderRadius: '0.5rem',
};

const defaultCenter = {
    lat: 17.3850, // Hyderabad default
    lng: 78.4867,
};

const options = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
};

export default function LocationPicker({
    initialLat,
    initialLng,
    onLocationSelect,
    className = ""
}) {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
    });

    const [marker, setMarker] = useState(null);
    const [address, setAddress] = useState('');
    const mapRef = useRef(null);
    const geocoder = useRef(null);

    // Initialize marker from props
    useEffect(() => {
        if (initialLat && initialLng) {
            const lat = parseFloat(initialLat);
            const lng = parseFloat(initialLng);
            if (!isNaN(lat) && !isNaN(lng)) {
                setMarker({ lat, lng });
            }
        }
    }, [initialLat, initialLng]);

    // Initialize geocoder once loaded
    useEffect(() => {
        if (isLoaded && !geocoder.current) {
            geocoder.current = new window.google.maps.Geocoder();
        }
    }, [isLoaded]);

    const onMapLoad = React.useCallback((map) => {
        mapRef.current = map;
        if (marker) {
            map.panTo(marker);
        }
    }, [marker]);

    // Helper to extract address components
    const parseAddressComponents = (components) => {
        let city = '';
        let state = '';
        let pincode = '';
        let area = '';

        components.forEach(component => {
            if (component.types.includes('locality')) city = component.long_name;
            if (component.types.includes('administrative_area_level_1')) state = component.long_name;
            if (component.types.includes('postal_code')) pincode = component.long_name;
            // area can be sublocality_level_1 or sublocality
            if (component.types.includes('sublocality_level_1') || component.types.includes('sublocality')) {
                area = component.long_name;
            }
            // fallback for city if locality is missing
            if (!city && component.types.includes('administrative_area_level_2')) city = component.long_name;
        });

        return { city, state, pincode, area };
    };

    // Handle map click / marker drag
    const handleLocationChange = async (latLng) => {
        const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
        const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;
        setMarker({ lat, lng });

        // Reverse Geocoding
        if (geocoder.current) {
            try {
                const response = await geocoder.current.geocode({ location: { lat, lng } });
                if (response.results[0]) {
                    const result = response.results[0];
                    const { city, state, pincode, area } = parseAddressComponents(result.address_components);

                    const locationData = {
                        lat,
                        lng,
                        full_address: result.formatted_address,
                        city,
                        state,
                        pincode,
                        area
                    };

                    setAddress(result.formatted_address);
                    onLocationSelect(locationData);
                }
            } catch (error) {
                console.error("Geocoding error:", error);
                onLocationSelect({ lat, lng });
            }
        }
    };

    const handleMapClick = (event) => {
        handleLocationChange(event.latLng);
    };

    const handleMarkerDragEnd = (event) => {
        handleLocationChange(event.latLng);
    };

    const getUserLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const latLng = new window.google.maps.LatLng(latitude, longitude);
                    handleLocationChange(latLng);
                    if (mapRef.current) {
                        mapRef.current.panTo({ lat: latitude, lng: longitude });
                        mapRef.current.setZoom(15);
                    }
                },
                () => {
                    toast.error("Unable to retrieve your location");
                }
            );
        }
    };

    if (loadError) return <div className="p-4 border border-red-200 bg-red-50 text-red-600 rounded">Error loading maps</div>;
    if (!isLoaded) return <div className="flex items-center justify-center p-8 bg-navy-50 rounded h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-navy-400" /></div>;

    return (
        <div className={`space-y-4 ${className}`}>
            <div className="relative z-10">
                <div className="absolute top-2 left-2 right-2 md:w-96">
                    <PlacesAutocomplete
                        onSelect={async (address) => {
                            try {
                                const results = await getGeocode({ address });
                                const result = results[0];
                                const { lat, lng } = await getLatLng(result);

                                setMarker({ lat, lng });
                                setAddress(result.formatted_address);

                                const { city, state, pincode, area } = parseAddressComponents(result.address_components);

                                onLocationSelect({
                                    lat,
                                    lng,
                                    full_address: result.formatted_address,
                                    city,
                                    state,
                                    pincode,
                                    area
                                });

                                if (mapRef.current) {
                                    mapRef.current.panTo({ lat, lng });
                                    mapRef.current.setZoom(15);
                                }
                            } catch (error) {
                                console.error("Error: ", error);
                            }
                        }}
                    />
                </div>
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-14 shadow-md bg-white hover:bg-navy-50"
                    onClick={getUserLocation}
                    title="Use My Location"
                >
                    <Navigation className="w-4 h-4 text-navy-700" />
                </Button>
            </div>

            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={marker ? 15 : 10}
                center={marker || defaultCenter}
                options={options}
                onLoad={onMapLoad}
                onClick={handleMapClick}
            >
                {marker && (
                    <MarkerF
                        position={marker}
                        draggable={true}
                        onDragEnd={handleMarkerDragEnd}
                    />
                )}
            </GoogleMap>

            {address && (
                <div className="text-sm text-navy-600 bg-navy-50 p-2 rounded flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-navy-400" />
                    {address}
                </div>
            )}
        </div>
    );
}

// Internal Search Component
const PlacesAutocomplete = ({ onSelect }) => {
    const {
        ready,
        value,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: {
            componentRestrictions: { country: 'in' }, // Restrict to India
        },
        debounce: 300,
    });

    const handleSelect = ({ description }) => {
        setValue(description, false);
        clearSuggestions();
        onSelect(description);
    };

    return (
        <div className="relative">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    disabled={!ready}
                    placeholder="Search location..."
                    className="pl-9 h-10 w-full bg-white shadow-md border-0 focus-visible:ring-0"
                />
            </div>
            {status === "OK" && (
                <ul className="absolute z-50 w-full bg-white mt-1 rounded-md shadow-lg border border-navy-100 max-h-60 overflow-auto">
                    {data.map(({ place_id, description }) => (
                        <li
                            key={place_id}
                            onClick={() => handleSelect({ description })}
                            className="px-4 py-2 hover:bg-navy-50 cursor-pointer text-sm text-navy-700 truncate border-b border-navy-50 last:border-0"
                        >
                            <MapPin className="inline w-3 h-3 mr-2 text-navy-400" />
                            {description}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
